/* eslint-disable react/prop-types */
import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Cookie } from '@/utils/cookie';

export type FallbackRenderProps = {
	error: Error;
	resetErrorBoundary: () => void;
};

export type JsmPlaygroundErrorBoundaryProps = PropsWithChildren<{
	activeTab?: string;
	teamType?: string;
	fallback?: ReactNode;
	fallbackRender?: (props: FallbackRenderProps) => ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	onReset?: () => void;
	resetKeys?: unknown[];
}>;

const getCanonicalUrl = (): string => {
	if (typeof document !== 'undefined') {
		const canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
		if (canonicalLink?.href) {
			return canonicalLink.href;
		}
	}
	if (typeof window !== 'undefined' && window.location?.href) {
		return window.location.href;
	}
	return '';
};

const getPagePath = (): string => {
	if (typeof window !== 'undefined' && window.location?.pathname) {
		return window.location.pathname;
	}
	return '';
};

const getLoginUserDetected = (): string => {
	if (typeof document !== 'undefined') {
		const cookie = Cookie.readCookie('login_user_detected');
		if (cookie) {
			return cookie;
		}
	}
	return 'false';
};

type InnerErrorBoundaryProps = {
	children: ReactNode;
	fallback?: ReactNode;
	fallbackRender?: (props: FallbackRenderProps) => ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	onReset?: () => void;
	resetKeys?: unknown[];
};

type InnerErrorBoundaryState = {
	hasError: boolean;
	error: Error | null;
};

class InnerErrorBoundary extends Component<InnerErrorBoundaryProps, InnerErrorBoundaryState> {
	public override state: InnerErrorBoundaryState = {
		hasError: false,
		error: null,
	};

	public static getDerivedStateFromError(error: Error): InnerErrorBoundaryState {
		return { hasError: true, error };
	}

	public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		this.props.onError?.(error, errorInfo);
	}

	public override componentDidUpdate(prevProps: InnerErrorBoundaryProps) {
		if (this.state.hasError && this.props.resetKeys) {
			const hasChanged =
				this.props.resetKeys.length !== prevProps.resetKeys?.length ||
				this.props.resetKeys.some((key, index) => key !== prevProps.resetKeys?.[index]);
			if (hasChanged) {
				this.resetErrorBoundary();
			}
		}
	}

	public resetErrorBoundary = () => {
		this.props.onReset?.();
		this.setState({ hasError: false, error: null });
	};

	public override render() {
		if (this.state.hasError && this.state.error) {
			if (this.props.fallbackRender) {
				return this.props.fallbackRender({
					error: this.state.error,
					resetErrorBoundary: this.resetErrorBoundary,
				});
			}
			if (this.props.fallback !== undefined) {
				return this.props.fallback;
			}
			return null;
		}

		return this.props.children;
	}
}

export const JsmPlaygroundErrorBoundary = ({
	children,
	activeTab,
	teamType = activeTab ?? 'it_support',
	fallback = null,
	fallbackRender,
	onError,
	onReset,
	resetKeys,
}: JsmPlaygroundErrorBoundaryProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleError = (error: Error, info: ErrorInfo) => {
		onError?.(error, info);

		createAnalyticsEvent({
			actionSubject: 'jsmPlaygroundSection',
			action: 'failed',
			actionSubjectId: 'errorBoundary',
			eventType: 'operational',
			source: 'jsm-playground',
			attributes: {
				trackEventName: 'playground-render-failed',
				eventContainer: 'jsm-playground',
				team_type: teamType.replace(/-/g, '_'),
				errorType: 'render_error',
				errorMessage: error?.message ?? String(error),
				componentStack: info?.componentStack ?? '',
				page_canonical_url: getCanonicalUrl(),
				page_path: getPagePath(),
				loginUserDetected: getLoginUserDetected(),
			},
		}).fire();
	};

	return (
		<InnerErrorBoundary
			resetKeys={resetKeys ?? [teamType]}
			onError={handleError}
			onReset={onReset}
			fallback={fallback}
			fallbackRender={fallbackRender}
		>
			{children}
		</InnerErrorBoundary>
	);
};

export default JsmPlaygroundErrorBoundary;
